import ReactECharts from 'echarts-for-react';
import { useEffect, useMemo, useState } from 'react';
import { Tooltip } from 'react-daisyui';
import { LuArrowRightLeft, LuBookOpenText, LuBot, LuUsers } from 'react-icons/lu';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ProjectStatsV2 } from '../../entities/analytics-v2';
import { AnomalousTaskDTO, ANOMALY_REASON_LABEL, ProjectFlowSummary } from '../../entities/task-flow';
import {
  getProjectAnomalies,
  getProjectFlowSummary,
  getProjectStatsV2,
  getTOPRiskyTasks,
} from '../../shared/api/dashboard';
import { useAIChat } from '../../shared/contexts/AIChat.context';
import { useSetDashboardHeader } from '../../shared/contexts/DashboardHeader.context';
import { useDateRange } from '../../shared/contexts/DateRange.provider';
import { useProjects } from '../../shared/contexts/Projects.provider';
import { formatRiskFlags, getTaskRiskReasonMeta } from '../../shared/utils/risk-flag-labels';
import { asISODate, fmt1, fmtDays, fmtPct, psiBadge, safeNum, trendArrow } from '../../shared/utils/stat-helpers';
import { InfoTip, KpiCard, PsiGauge, StatEmpty, StatError, StatLoading } from '../../widgets/stats';
import TransferModal from '../../widgets/transfer/TransferModal';
import { ActionsMenu } from '../../widgets/ui/ActionsMenu';
import { StatInsightsPanel } from './StatAIPanel';

export function ProjectChart() {
  const navigate = useNavigate();
  const dateRangeCtx = useDateRange();
  const { projectId } = useParams();
  const setDashHeader = useSetDashboardHeader();
  const { openChat } = useAIChat();
  const projects = useProjects();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [stat, setStat] = useState<ProjectStatsV2 | null>(null);
  const [riskTasks, setRiskTasks] = useState<any[]>([]);
  const [riskLoading, setRiskLoading] = useState(false);

  const [flowSummary, setFlowSummary] = useState<ProjectFlowSummary | null>(null);
  const [flowLoading, setFlowLoading] = useState(false);
  const [anomalies, setAnomalies] = useState<AnomalousTaskDTO[]>([]);
  const [anomaliesLoading, setAnomaliesLoading] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({ from: '', to: '' });

  const [selectedKey, setSelectedKey] = useState<
    'created' | 'completed' | 'reopens' | 'throughput' | 'reopenRate' | 'cycle' | 'wip'
  >('completed');

  useEffect(() => {
    if (dateRangeCtx?.from && dateRangeCtx?.to) {
      setDateRange({ from: asISODate(dateRangeCtx.from), to: asISODate(dateRangeCtx.to) });
    }
  }, [dateRangeCtx?.from, dateRangeCtx?.to]);

  useEffect(() => {
    const run = async () => {
      if (!projectId || !dateRange || !dateRange.from || !dateRange.to) return;

      try {
        setLoading(true);
        setError(null);

        const { stat: loaded } = await getProjectStatsV2(projectId, dateRange);
        setStat(loaded);
      } catch (e) {
        console.error(e);
        setError('Failed to load project stats.');
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [projectId, dateRange, dateRange.from, dateRange.to]);

  useEffect(() => {
    if (!projectId || !dateRangeCtx?.from || !dateRangeCtx?.to) return;
    (async () => {
      setRiskLoading(true);
      try {
        const { top } = await getTOPRiskyTasks(projectId, { from: dateRangeCtx.from!, to: dateRangeCtx.to! });
        setRiskTasks(top);
      } finally {
        setRiskLoading(false);
      }
    })();
  }, [projectId, dateRangeCtx?.from, dateRangeCtx?.to]);

  useEffect(() => {
    if (!projectId || !dateRange.from || !dateRange.to) return;
    setFlowLoading(true);
    setAnomaliesLoading(true);
    getProjectFlowSummary(projectId, dateRange)
      .then(({ stat }) => setFlowSummary(stat))
      .catch(() => setFlowSummary(null))
      .finally(() => setFlowLoading(false));
    getProjectAnomalies(projectId, dateRange, 10)
      .then(({ tasks }) => setAnomalies(tasks))
      .catch(() => setAnomalies([]))
      .finally(() => setAnomaliesLoading(false));
  }, [projectId, dateRange.from, dateRange.to]);

  const goUsers = () => navigate(`/dashboard/projects/${projectId}/users`);

  const summary = stat?.summary;
  const delta = stat?.trend?.delta;

  const psi = safeNum(stat?.psi, 0);

  const createdCount = safeNum(summary?.createdCount);
  const completedCount = safeNum(summary?.completedCount);

  const throughput = safeNum(summary?.throughputAccuracyPercent);
  const reopenRate = safeNum(summary?.reopenRatePercent);
  const avgCycleHours = safeNum(summary?.avgCycleTimeHours);
  const bugRatio = safeNum(summary?.bugRatioPercent);
  const avgWip = safeNum(summary?.avgWipCount);
  const flowEff = safeNum(summary?.flowEfficiencyPercent);

  const aThroughput = trendArrow(safeNum(delta?.throughputAccuracyPercent), false);
  const aReopen = trendArrow(safeNum(delta?.reopenRatePercent), true);
  const aCycle = trendArrow(safeNum(delta?.avgCycleTimeHours), true);

  const timelineOption = useMemo(() => {
    const series = stat?.series ?? [];
    const days = series.map((s) => {
      const d = new Date(s.day);
      return isNaN(d.getTime()) ? String(s.day) : d.toLocaleDateString();
    });

    const createdS = series.map((s) => safeNum(s.createdCount));
    const completedS = series.map((s) => safeNum(s.completedCount));
    const reopensS = series.map((s) => safeNum(s.reopenEvents));

    const pick = () => {
      switch (selectedKey) {
        case 'created':
          return { name: 'Created', type: 'bar' as const, data: createdS, suffix: '' };
        case 'completed':
          return { name: 'Completed', type: 'bar' as const, data: completedS, suffix: '' };
        case 'reopens':
          return { name: 'Reopens', type: 'bar' as const, data: reopensS, suffix: '' };
        case 'throughput':
          return {
            name: 'Throughput',
            type: 'line' as const,
            data: series.map((s) => {
              const c = safeNum(s.completedCount);
              const cr = safeNum(s.createdCount);
              return cr > 0 ? Math.round((c / cr) * 100) : 0;
            }),
            suffix: '%',
          };
        case 'reopenRate':
          return {
            name: 'Reopen rate',
            type: 'line' as const,
            data: series.map((s) => {
              const r = safeNum(s.reopenEvents);
              const c = safeNum(s.completedCount);
              return c > 0 ? Math.round((r / c) * 100) : 0;
            }),
            suffix: '%',
          };
        case 'cycle':
          return {
            name: 'Avg cycle (range)',
            type: 'line' as const,
            data: series.map(() => fmt1(avgCycleHours / 24)),
            suffix: 'd',
          };
        case 'wip':
          return {
            name: 'WIP snapshot',
            type: 'line' as const,
            data: series.map((s) => safeNum(s.wipSnapshot)),
            suffix: '',
          };
      }
    };

    const s = pick();

    return {
      tooltip: { trigger: 'axis' },
      legend: { bottom: 0, left: 'center' },
      grid: { left: 24, right: 16, top: 16, bottom: 44, containLabel: true },
      xAxis: { type: 'category', data: days, axisLabel: { interval: 'auto' } },
      yAxis: { type: 'value' },
      series: [
        {
          name: s.name,
          type: s.type,
          data: s.data,
          smooth: s.type === 'line',
          symbolSize: 6,
          areaStyle: s.type === 'line' ? { opacity: 0.08 } : undefined,
          barWidth: s.type === 'bar' ? 10 : undefined,
        },
      ],
    };
  }, [stat?.series, selectedKey, avgCycleHours]);

  const progressDonutOption = useMemo(() => {
    const total = createdCount;
    const done = completedCount;
    const remaining = Math.max(0, total - done);

    return {
      tooltip: { trigger: 'item' },
      series: [
        {
          type: 'pie',
          radius: ['55%', '78%'],
          label: { formatter: (p: any) => `${p.name}\n${p.value}` },
          data: [
            { name: 'Completed', value: done },
            { name: 'Remaining', value: remaining },
          ],
          itemStyle: { borderRadius: 10, borderWidth: 2, borderColor: '#0000' },
        },
      ],
    };
  }, [createdCount, completedCount]);

  const bottlenecksOption = useMemo(() => {
    const list = stat?.bottlenecks ?? [];
    const top = [...list].sort((a, b) => safeNum(b.avgDays) - safeNum(a.avgDays)).slice(0, 10);

    return {
      tooltip: { trigger: 'item', formatter: (p: any) => `<b>${p.name}</b><br/>Avg: ${fmt1(p.value)} days` },
      grid: { left: 140, right: 16, top: 10, bottom: 20 },
      xAxis: { type: 'value' },
      yAxis: { type: 'category', data: top.map((x) => x.status), axisLabel: { width: 130, overflow: 'truncate' } },
      series: [
        {
          type: 'bar',
          data: top.map((x) => safeNum(x.avgDays)),
          barWidth: 14,
          itemStyle: { borderRadius: 8 },
        },
      ],
    };
  }, [stat?.bottlenecks]);

  const rolesLoadOption = useMemo(() => {
    const list = stat?.rolesLoad ?? [];
    const top = [...list].sort((a, b) => safeNum(b.utilization) - safeNum(a.utilization)).slice(0, 12);

    return {
      tooltip: { trigger: 'item', formatter: (p: any) => `<b>${p.name}</b><br/>Utilization: ${fmt1(p.value)}%` },
      grid: { left: 140, right: 16, top: 10, bottom: 20 },
      xAxis: { type: 'value', max: 120 },
      yAxis: { type: 'category', data: top.map((x) => x.role), axisLabel: { width: 130, overflow: 'truncate' } },
      series: [
        { type: 'bar', data: top.map((x) => safeNum(x.utilization)), barWidth: 14, itemStyle: { borderRadius: 8 } },
      ],
    };
  }, [stat?.rolesLoad]);

  const goUser = (userId: string) => {
    navigate(`/dashboard/projects/${projectId}/users/${userId}`);
  };

  const headerTitle =
    projects?.find((p) => projectId === p._id)?.name ??
    (stat?.projectName || `Project ${stat?.projectId ?? projectId}`);

  const rangeLabel = `${new Date(dateRangeCtx?.from || '').toLocaleDateString()} → ${new Date(dateRangeCtx?.to || '').toLocaleDateString()}`;

  useEffect(() => {
    setDashHeader({
      component: 'project',
      breadcrumb: (
        <Link to="/dashboard/projects" className="btn btn-sm btn-ghost px-0 h-auto min-h-0">
          <Tooltip message={'Projects'} position={'bottom'}>
            <span className="text-xl">←</span>
          </Tooltip>
        </Link>
      ),
      title: stat ? (
        <span className="flex items-center gap-2">
          {headerTitle}
          <span className={`badge ${psiBadge(psi).cls} text-sm`}>
            PSI {psi} · {psiBadge(psi).label}
          </span>
        </span>
      ) : (
        headerTitle
      ),
      subtitle: stat ? (
        <span>
          Range: <span className="font-medium">{rangeLabel}</span>
        </span>
      ) : undefined,
      actions: (
        <div className="flex items-center gap-2">
          <button
            className="btn btn-primary btn-sm shadow-glow gap-1.5"
            onClick={() =>
              openChat({
                projectId,
                suggestedPrompts: [
                  'What are the main delivery risks on this project?',
                  'Which tasks should I prioritize?',
                  ...(reopenRate > 15 ? ['Why is the reopen rate high?'] : []),
                  ...(stat?.bottlenecks?.length ? ['Explain the main bottleneck'] : []),
                  ...(avgCycleHours / 24 > 7 ? ['What is causing long cycle times?'] : []),
                ],
                insightText: stat?.aiInsightPayload?.notes?.riskFlags?.length
                  ? formatRiskFlags(stat.aiInsightPayload.notes.riskFlags)
                  : undefined,
                analyticsContext: {
                  scope: 'project',
                  projectIds: projectId ? [projectId] : undefined,
                  dateRange:
                    dateRangeCtx?.from && dateRangeCtx?.to
                      ? {
                          from: dateRangeCtx.from.toISOString().split('T')[0],
                          to: dateRangeCtx.to.toISOString().split('T')[0],
                        }
                      : undefined,
                },
              })
            }
          >
            <LuBot size={14} />
            Ask mFlow
          </button>
          <ActionsMenu
            items={[
              { label: 'Open users', icon: LuUsers, onClick: goUsers },
              { label: 'Knowledge', icon: LuBookOpenText, to: `/dashboard/projects/${projectId}/knowledge` },
              { label: 'Transfer…', icon: LuArrowRightLeft, onClick: () => setIsTransferModalOpen(true) },
            ]}
          />
        </div>
      ),
    });
    return () => setDashHeader({});
  }, [stat, psi, rangeLabel, setDashHeader]);

  if (loading) return <StatLoading />;
  if (error) return <StatError message={error} />;
  if (!stat) return <StatEmpty message="No project stats." />;

  return (
    <div className="min-h-screen bg-base-200">
      <div className="p-4 space-y-4">
        {/* AI summary + chat */}
        <StatInsightsPanel
          riskFlags={stat.aiInsightPayload?.notes?.riskFlags}
          sentiment={(stat as any).aiSummary?.sentiment || 'neutral'}
          riskLevel={(stat as any).aiSummary?.riskLevel || 'medium'}
          statId={(stat as any)._id}
        />

        {/* KPI strip */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {/* PSI */}
          <KpiCard
            label={
              <>
                PSI
                <InfoTip
                  text={
                    'Project Stability Index (0–100)\n\nCombines:\n- Throughput (completed / created)\n- Quality (reopens)\n- Flow (cycle time + efficiency)\n\nHigher = more stable.'
                  }
                />
              </>
            }
          >
            <div className="grid grid-cols-2 gap-2 mt-2 items-center">
              <div className="text-3xl font-semibold">{psi}</div>
              <PsiGauge value={psi} />
            </div>
          </KpiCard>

          {/* Scope */}
          <KpiCard
            label={
              <>
                Scope
                <InfoTip text={'Created tasks in range = incoming scope.\nCompleted tasks in range = throughput.'} />
              </>
            }
            value={createdCount}
            sub={`Completed: ${completedCount}`}
            footer={`Reopens: ${safeNum(summary?.reopenEvents)}`}
          />

          {/* Throughput */}
          <KpiCard
            label={
              <>
                Throughput
                <InfoTip
                  text={
                    'Throughput accuracy = completedCount / createdCount.\nShows how much incoming work is actually finished in the selected range.'
                  }
                />
              </>
            }
            footer="Completed / Created"
          >
            <div className="flex items-end gap-2">
              <div className="text-3xl font-semibold">{fmtPct(throughput)}</div>
              <div className={`text-sm ${aThroughput.cls}`}>
                {aThroughput.s} {fmtPct(safeNum(delta?.throughputAccuracyPercent))}
              </div>
            </div>
          </KpiCard>

          {/* Reopen */}
          <KpiCard
            label={
              <>
                Reopen rate
                <InfoTip text={'Reopen rate = reopenEvents / completedCount\nLower is better.'} />
              </>
            }
            footer="Quality / clarity"
          >
            <div className="flex items-end gap-2">
              <div className="text-3xl font-semibold">{fmtPct(reopenRate)}</div>
              <div className={`text-sm ${aReopen.cls}`}>
                {aReopen.s} {fmtPct(safeNum(delta?.reopenRatePercent))}
              </div>
            </div>
          </KpiCard>

          {/* Cycle time */}
          <KpiCard
            label={
              <>
                Cycle time
                <InfoTip text={'Avg cycle time = average (createdAt → doneAt). Lower is better.'} />
              </>
            }
            footer="Avg across completed tasks"
          >
            <div className="text-sm mt-1">
              <span className="font-semibold text-xl">{fmtDays(avgCycleHours / 24)}</span>{' '}
              <span className={`ml-2 text-sm ${aCycle.cls}`}>
                {aCycle.s} {fmtDays(safeNum(delta?.avgCycleTimeHours) / 24)}
              </span>
            </div>
          </KpiCard>
        </div>

        {/* V2 metrics: Bug ratio, WIP, Flow efficiency */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <KpiCard
            label={
              <>
                Bug ratio
                <InfoTip
                  text={'Bug ratio = issues of type "Bug" / total created issues.\nGood: < 20%. Flag if > 30%.'}
                />
              </>
            }
            footer="Quality at intake"
          >
            <div
              className={`text-3xl font-semibold ${bugRatio > 30 ? 'text-error' : bugRatio > 20 ? 'text-warning' : ''}`}
            >
              {bugRatio > 0 ? fmtPct(bugRatio) : '—'}
            </div>
            {bugRatio > 0 && (
              <div className="text-xs mt-1 opacity-70">
                {safeNum(summary?.bugCount ?? 0)} bug{(summary?.bugCount ?? 0) !== 1 ? 's' : ''} / {createdCount}{' '}
                created
              </div>
            )}
          </KpiCard>

          <KpiCard
            label={
              <>
                Avg WIP
                <InfoTip
                  text={
                    'Average number of in-progress issues over the date range.\nHigher WIP = more context switching and slower flow.'
                  }
                />
              </>
            }
            footer="In-progress tasks (avg)"
          >
            <div className="text-3xl font-semibold">{avgWip > 0 ? fmt1(avgWip) : '—'}</div>
          </KpiCard>

          <KpiCard
            label={
              <>
                Flow efficiency
                <InfoTip
                  text={
                    'Time in active statuses / total cycle time.\nIndustry avg: 15–40%. < 15% = lots of waiting. > 60% = very healthy.'
                  }
                />
              </>
            }
            footer="Active time / total cycle time"
          >
            <div
              className={`text-3xl font-semibold ${flowEff > 60 ? 'text-success' : flowEff > 0 && flowEff < 15 ? 'text-warning' : ''}`}
            >
              {flowEff > 0 ? fmtPct(flowEff) : '—'}
            </div>
          </KpiCard>
        </div>

        {/* Top risk tasks */}
        <div className="card bg-base-100 shadow-card">
          <div className="card-body p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-lg font-semibold">Top risk tasks</div>
                <div className="text-sm opacity-70">Most likely to delay delivery</div>
              </div>
            </div>

            {riskLoading ? (
              <div className="py-6 flex justify-center">
                <div className="loading loading-spinner" />
              </div>
            ) : !riskTasks.length ? (
              <div className="py-6 opacity-70">No risky tasks detected in this range.</div>
            ) : (
              <div className="overflow-x-auto mt-3">
                <table className="table table-zebra w-full">
                  <thead>
                    <tr>
                      <th>Issue</th>
                      <th>Summary</th>
                      <th>Assignee</th>
                      <th>Status</th>
                      <th className="text-right">Risk</th>
                      <th>Reasons</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {riskTasks.map((t) => (
                      <tr key={t.issueId}>
                        <td className="font-mono">
                          {t.url ? (
                            <a href={t.url} target="_blank" rel="noreferrer">
                              {t.issueKey ?? t.issueId}
                              <span className="ml-1 opacity-50">🔗</span>
                            </a>
                          ) : (
                            (t.issueKey ?? t.issueId)
                          )}
                        </td>
                        <td className="max-w-[420px] truncate">{t.summary ?? '—'}</td>
                        <td>{t.assigneeName ?? 'Unassigned'}</td>
                        <td>{t.status ?? '—'}</td>
                        <td className="text-right">
                          <span
                            className={`badge ${t.riskScore >= 70 ? 'badge-error' : t.riskScore >= 40 ? 'badge-warning' : 'badge-success'}`}
                          >
                            {t.riskScore}
                          </span>
                        </td>
                        <td className="space-x-1">
                          {(t.riskReasons ?? []).slice(0, 3).map((r: any) => (
                            <span key={r} className="badge badge-ghost" title={getTaskRiskReasonMeta(r).description}>
                              {getTaskRiskReasonMeta(r).label}
                            </span>
                          ))}
                        </td>
                        <td>
                          <button
                            className="btn btn-ghost btn-xs"
                            onClick={() =>
                              navigate(`/dashboard/projects/${projectId}/tasks/${t.issueId}`, {
                                state: {
                                  issueKey: t.issueKey,
                                  summary: t.summary,
                                  status: t.status,
                                  assigneeName: t.assigneeName,
                                  url: t.url,
                                  riskScore: t.riskScore,
                                  riskReasons: t.riskReasons,
                                },
                              })
                            }
                          >
                            Open
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Flow & bottlenecks */}
        <div className="card bg-base-100 shadow-card">
          <div className="card-body p-4">
            <div className="text-lg font-semibold flex items-center">
              Flow & bottlenecks
              <InfoTip
                text={
                  'Time spent in each workflow status, computed from the Jira changelog.\n\nBottleneck = the status with the highest average time-in-status.'
                }
              />
            </div>
            <div className="text-sm opacity-70">Where tasks actually spend their time</div>

            {flowLoading ? (
              <div className="py-6 flex justify-center">
                <div className="loading loading-spinner" />
              </div>
            ) : !flowSummary || flowSummary.statusDurations.length === 0 ? (
              <div className="py-6 opacity-70">No status transitions in this range.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                <div className="overflow-x-auto">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th className="text-right">Avg</th>
                        <th className="text-right">Median</th>
                        <th className="text-right">Samples</th>
                      </tr>
                    </thead>
                    <tbody>
                      {flowSummary.statusDurations.map((s) => (
                        <tr key={s.status} className={s.status === flowSummary.bottleneckStatus ? 'bg-warning/10' : ''}>
                          <td className="font-medium">
                            {s.status}
                            {s.status === flowSummary.bottleneckStatus && (
                              <span className="badge badge-warning badge-xs ml-2">bottleneck</span>
                            )}
                          </td>
                          <td className="text-right">{fmtDays(s.avgDays)}</td>
                          <td className="text-right">{fmtDays(s.medianDays)}</td>
                          <td className="text-right opacity-60">{s.sampleCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div>
                  <div className="text-xs font-semibold opacity-50 uppercase tracking-wide mb-1">
                    Most common transitions
                  </div>
                  {flowSummary.transitionFrequency.length === 0 ? (
                    <div className="text-sm opacity-60">No transitions recorded.</div>
                  ) : (
                    <ul className="text-sm space-y-1">
                      {flowSummary.transitionFrequency.slice(0, 8).map((t, i) => (
                        <li key={i} className="flex items-center justify-between gap-2">
                          <span className="truncate">
                            {t.from} → {t.to}
                          </span>
                          <span className="opacity-60 shrink-0">{t.count}×</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Anomalies */}
        <div className="card bg-base-100 shadow-card">
          <div className="card-body p-4">
            <div className="text-lg font-semibold flex items-center">
              Anomalies
              <InfoTip
                text={
                  'Open tasks whose time in their current status is ≥2x the project’s typical (median) for that status, or that reopen/reassign unusually often.'
                }
              />
            </div>
            <div className="text-sm opacity-70">Tasks that deviate from the project&apos;s normal flow</div>

            {anomaliesLoading ? (
              <div className="py-6 flex justify-center">
                <div className="loading loading-spinner" />
              </div>
            ) : !anomalies.length ? (
              <div className="py-6 opacity-70">No anomalies detected in this range.</div>
            ) : (
              <div className="overflow-x-auto mt-3">
                <table className="table table-zebra w-full">
                  <thead>
                    <tr>
                      <th>Issue</th>
                      <th>Summary</th>
                      <th>Status</th>
                      <th className="text-right">Days in status</th>
                      <th className="text-right">vs. typical</th>
                      <th>Reasons</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {anomalies.map((t) => (
                      <tr key={t.issueId}>
                        <td className="font-mono">{t.issueKey ?? t.issueId}</td>
                        <td className="max-w-[360px] truncate">{t.summary ?? '—'}</td>
                        <td>{t.status ?? '—'}</td>
                        <td className="text-right">{fmt1(t.daysInCurrentStatus)}</td>
                        <td className="text-right">
                          {t.deviationRatio != null ? (
                            <span className={t.deviationRatio >= 3 ? 'text-error font-medium' : 'text-warning'}>
                              {fmt1(t.deviationRatio)}×
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="space-x-1">
                          {t.anomalyReasons.map((r) => (
                            <span key={r} className="badge badge-ghost">
                              {ANOMALY_REASON_LABEL[r]}
                            </span>
                          ))}
                        </td>
                        <td>
                          <button
                            className="btn btn-ghost btn-xs"
                            onClick={() =>
                              navigate(`/dashboard/projects/${projectId}/tasks/${t.issueId}`, {
                                state: {
                                  issueKey: t.issueKey,
                                  summary: t.summary,
                                  status: t.status,
                                  anomalyReasons: t.anomalyReasons,
                                },
                              })
                            }
                          >
                            Open
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Main visuals */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* Progress donut */}
          <div className="card bg-base-100 shadow-card">
            <div className="card-body p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-lg font-semibold">Progress snapshot</div>
                  <div className="text-sm opacity-70">Created vs completed (range)</div>
                </div>
                <button className="btn btn-sm btn-outline" onClick={goUsers}>
                  Drill to users
                </button>
              </div>

              <div className="h-[280px]">
                <ReactECharts option={progressDonutOption} style={{ height: '100%' }} onEvents={{ click: goUsers }} />
              </div>
              <div className="text-xs opacity-60">Click chart to open users stats.</div>
            </div>
          </div>

          {/* Timeline */}
          <div className="card bg-base-100 shadow-card lg:col-span-2">
            <div className="card-body p-4">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="text-lg font-semibold flex items-center">
                    Trend
                    <InfoTip
                      text={'Daily time series in the selected range.\nSwitch metric to inspect stability & flow.'}
                    />
                  </div>
                  <div className="text-sm opacity-70">Daily series inside selected range</div>
                </div>

                <label className="form-control w-full md:w-60">
                  <span className="label-text text-xs opacity-70">Metric</span>
                  <select
                    className="select select-bordered select-sm bg-base-100"
                    value={selectedKey}
                    onChange={(e) => setSelectedKey(e.target.value as typeof selectedKey)}
                  >
                    {(
                      [
                        ['created', 'Created'],
                        ['completed', 'Completed'],
                        ['reopens', 'Reopens'],
                        ['throughput', 'Throughput %'],
                        ['reopenRate', 'Reopen %'],
                        ['cycle', 'Cycle (range)'],
                        ['wip', 'WIP snapshot'],
                      ] as const
                    ).map(([k, label]) => (
                      <option key={k} value={k}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="h-[340px] mt-2">
                <ReactECharts option={timelineOption} style={{ height: '100%' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Bottlenecks & roles load */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="card bg-base-100 shadow-card">
            <div className="card-body p-4">
              <div className="text-lg font-semibold flex items-center">
                Bottlenecks
                <InfoTip
                  text={'Average time spent in each status (from changelog).\nTop statuses where tasks wait most.'}
                />
              </div>

              {stat.bottlenecks?.length ? (
                <div className="h-[280px]">
                  <ReactECharts option={bottlenecksOption} style={{ height: '100%' }} />
                </div>
              ) : (
                <div className="p-6 opacity-70">No bottleneck data for this range.</div>
              )}
            </div>
          </div>

          <div className="card bg-base-100 shadow-card">
            <div className="card-body p-4">
              <div className="text-lg font-semibold flex items-center">
                Roles load
                <InfoTip text={'Utilization by role (MVP approximation).\nUsed to spot overloaded roles fast.'} />
              </div>

              {stat.rolesLoad?.length ? (
                <div className="h-[280px]">
                  <ReactECharts option={rolesLoadOption} style={{ height: '100%' }} />
                </div>
              ) : (
                <div className="p-6 opacity-70">No roles load data for this range.</div>
              )}
            </div>
          </div>
        </div>

        {/* Top users */}
        <div className="card bg-base-100 shadow-card">
          <div className="card-body p-0">
            <div className="p-4 flex items-center justify-between">
              <div>
                <div className="text-lg font-semibold flex items-center">
                  Top contributors
                  <InfoTip text={'Users ranked by completed tasks in this range.\nShows output + quality + speed.'} />
                </div>
                <div className="text-sm opacity-70">Click &#34;Users&#34; to drill down.</div>
              </div>
              <button className="btn btn-sm btn-outline" onClick={goUsers}>
                Users →
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th>User</th>
                    <th className="text-right">Done</th>
                    <th className="text-right">Reopen</th>
                    <th className="text-right">Cycle</th>
                  </tr>
                </thead>
                <tbody>
                  {(stat.topUsersByCompleted ?? []).slice(0, 30).map((u) => (
                    <tr key={u.userId} className="hover cursor-pointer" onClick={() => goUser(u.userId)}>
                      <td className="font-medium">{u.userName || u.userId}</td>
                      <td className="text-right">{safeNum(u.completedCount)}</td>
                      <td className="text-right">{fmtPct(safeNum(u.reopenRatePercent))}</td>
                      <td className="text-right">{fmtDays(safeNum(u.avgCycleTimeHours) / 24)}</td>
                    </tr>
                  ))}
                  {!stat.topUsersByCompleted?.length && (
                    <tr>
                      <td colSpan={4} className="text-center opacity-60 py-6">
                        No completed tasks in this range.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="p-4 text-xs opacity-60">Fractal drill-down: Studio → Projects → Project → Users</div>
          </div>
        </div>
      </div>

      {isTransferModalOpen && projectId && (
        <TransferModal
          sourceProvider="jira"
          sourceProjectId={projectId}
          sourceProjectName={headerTitle}
          onClose={() => setIsTransferModalOpen(false)}
        />
      )}
    </div>
  );
}
